
import globalModule from '../../global';
import performanceSupported from '../performance-mark-check/performance-mark-check';

/**
 * Platform controls how the `global` and `globalDelayed` code is ran within the project
 * It is abstracted away to this class so that any frontend dev can just work directly in the module files for 
 * `global.js` and `globalDelayed.js`
 * 
 * @example
 * const R = new Platform();
 * R.init();
 */
class Platform {

    /**
     * Runs the `globalModule()` and performs performance marks for `main js`
     * @returns {Promise} returns the promise when complete
     * @requires module:global
     */
    runGlobal() {
        return globalModule().then(() => {
            // for internal performance measurements
            if (performanceSupported()) {
                performance.mark("main js end");
                performance.measure("main js", "main js start", "main js end");
            }
        });
    }

    /**
     * Runs the `globalDelayed` and performs performance marks for `delayed js`
     * @returns {Promise} returns the promise when complete
     * @requires module:globalDelayed
     */
    runGlobalDelayed() {
        /* webpackPrefetch: true, webpackChunkName: "setupGlobalDelayed" */
        return import('../../globalDelayed').then(delayedCode => {
            // Initialize the delayed code
            if (performanceSupported()) {
                performance.mark("delayed js start");
            }
            delayedCode.default()
                .then(() => {
                    if (performanceSupported()) {
                        performance.mark("delayed js end");
                        performance.measure("delayed js", "delayed js start", "delayed js end");
                    }
                });
        });
    }

    /**
     * The `init()` method is incharge of running the `runGlobal()` and `runGlobalDelayed()` at the correct times
     * 
     * `runGlobal()` is ran straight away. It represents the critical JavaScript that needs to function
     * `runGlobalDelayed()` is ran after `window.load` and as a microtask. It represents any other JavaScript that can wait or is below the fold
     */
    init() {
        window.addEventListener('load', () => {
            setTimeout(() => {
                this.runGlobalDelayed();
            }, 0);
        }, false);

        this.runGlobal();
    }
}

export default Platform;